import type { Process } from '../../types';
import type { Segment } from '../../migrations/legacyEdge';
import { buildEdgeMap, resolveProcessEdges } from '../processEdges';

export type SemanticNodeOperator = 'normal' | 'if' | 'parallel' | 'exception' | 'wait';

export interface SegmentSemanticStyle {
  dashArray?: string;
  lineOpacity?: number;
  strokeWidth?: number;
  forceParallel?: boolean;
}

function segmentOperator(segment: Segment): SemanticNodeOperator {
  const op = segment.flowType ?? 'normal';
  if (op === 'branch') return 'if';
  if (op === 'parallel') return 'parallel';
  if (op === 'fail') return 'exception';
  if (op === 'wait') return 'wait';
  return 'normal';
}

export function segmentSemanticStyle(segment: Segment): SegmentSemanticStyle {
  const op = segmentOperator(segment);
  const isAsync = segment.blocking === 'non-blocking';

  const base: SegmentSemanticStyle = {
    dashArray: isAsync ? '3 5' : undefined,
    strokeWidth: op === 'exception' ? 3 : undefined,
    lineOpacity: op === 'exception' ? 0.95 : undefined,
  };

  if (op === 'parallel') {
    return {
      ...base,
      forceParallel: true,
      dashArray: isAsync ? '5 5' : undefined,
    };
  }

  return base;
}

export function buildNodeSemanticMap(processes: Process[], edges: Segment[]): Record<string, SemanticNodeOperator> {
  const byNode: Record<string, SemanticNodeOperator> = {};
  const edgeMap = buildEdgeMap(edges);
  const rank: Record<SemanticNodeOperator, number> = {
    normal: 0,
    if: 1,
    parallel: 2,
    exception: 3,
    wait: 4,
  };

  processes.forEach(process => {
    if (process.visible === false) return;
    resolveProcessEdges(process, edgeMap).forEach(segment => {
      const op = segmentOperator(segment);
      if (rank[op] > rank[byNode[segment.from] ?? 'normal']) byNode[segment.from] = op;
      if (rank[op] > rank[byNode[segment.to] ?? 'normal']) byNode[segment.to] = op;
    });
  });

  return byNode;
}
